import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Save, Archive, RotateCcw, Trash2, Plus, ArrowUpRight, GitBranch,
  FlaskConical, History, MapPin, Link2, X, Mic, FileDown, Pencil, Folder, Copy,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, apiError } from "@/lib/api";
import { safeHref } from "@/lib/utils";
import { Loading, EmptyState, Badge, Btn, Input, Select, Textarea, Field, Panel, ContactMethodsEditor } from "@/components/common";
import DictateButton from "@/components/DictateButton";
import Modal from "@/components/Modal";
import ContactWeb from "@/components/ContactWeb";
import ConnectionModal from "@/components/ConnectionModal";
import VoiceContactModal from "@/components/VoiceContactModal";
import VoiceEditModal from "@/components/VoiceEditModal";
import ContactProfile from "@/components/ContactProfile";
import { EmailButton } from "@/components/EmailCompose";

const ETYPES = ["person", "business", "organization", "place", "group", "event"];
const VERIFS = ["unverified", "uncertain", "confirmed", "disputed", "outdated"];
const STAGES = ["candidate", "verified_identity", "documented_connection", "established_relationship", "communication_permitted"];
const PROFILE_TABS = ["Overview", "Background", "Interests", "Personality", "Notes & Timeline", "Importance"];
const TABS = ["Overview", "Background", "Interests", "Personality", "Notes & Timeline", "Importance", "Identity", "Memberships", "Groups", "Web", "Relationships", "Evidence", "Activity", "History"];
const PROFILE_SECTION = { "Overview": "overview", "Background": "background", "Interests": "interests", "Personality": "personality", "Notes & Timeline": "notes", "Importance": "importance" };

export default function EntityDetail() {
  const { id } = useParams();
  const { workspaceId, canEdit, isOwner } = useAuth();
  const navigate = useNavigate();
  const [full, setFull] = useState(null);
  const [form, setForm] = useState(null);
  const [tab, setTab] = useState("Overview");
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [audit, setAudit] = useState([]);
  const [interests, setInterests] = useState([]);
  const [relOpen, setRelOpen] = useState(false);
  const [relEdit, setRelEdit] = useState(null);
  const [evOpen, setEvOpen] = useState(false);
  const [evEdit, setEvEdit] = useState(null);
  const [memOpen, setMemOpen] = useState(false);
  const [groupAddOpen, setGroupAddOpen] = useState(false);
  const [voiceOpen, setVoiceOpen] = useState(false);
  const [voiceEditOpen, setVoiceEditOpen] = useState(false);
  const [delPreview, setDelPreview] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get(`/entities/${id}/full?workspace_id=${workspaceId}`);
    setFull(data);
    setForm({ ...data.entity, tagsStr: (data.entity.tags || []).join(", "), aliasesStr: (data.entity.aliases || []).join(", "), websitesStr: (data.entity.websites || []).join(", ") });
    setDirty(false);
  }, [id, workspaceId]);

  const loadAudit = useCallback(async () => {
    const { data } = await api.get(`/audit?workspace_id=${workspaceId}&target_id=${id}`);
    setAudit(data.items);
  }, [id, workspaceId]);

  useEffect(() => { setFull(null); load(); }, [load]);
  useEffect(() => { if (tab === "History") loadAudit(); }, [tab, loadAudit]);
  useEffect(() => { api.get(`/strategic-interests?workspace_id=${workspaceId}`).then((r) => setInterests(r.data)).catch(() => {}); }, [workspaceId]);

  useEffect(() => {
    if (!dirty) return;
    const handler = (ev) => { ev.preventDefault(); ev.returnValue = ""; };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  const set = (k, v) => { setForm((p) => ({ ...p, [k]: v })); setDirty(true); };

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        name: form.name, type: form.type, description: form.description,
        aliases: form.aliasesStr.split(",").map((t) => t.trim()).filter(Boolean),
        tags: form.tagsStr.split(",").map((t) => t.trim()).filter(Boolean),
        websites: form.websitesStr.split(",").map((t) => t.trim()).filter(Boolean),
        relevance_notes: form.relevance_notes || "", contact_methods: form.contact_methods || [],
        address: form.address || "", lat: form.lat === "" || form.lat == null ? null : parseFloat(form.lat),
        lng: form.lng === "" || form.lng == null ? null : parseFloat(form.lng),
        external_refs: form.external_refs || [], verification_status: form.verification_status,
        relationship_stage: form.relationship_stage, communication_preference: form.communication_preference || "",
        do_not_contact: !!form.do_not_contact, notes: form.notes || "", meaningful_dates: form.meaningful_dates || [],
        strategic_interest_ids: form.strategic_interest_ids || [],
      };
      await api.put(`/entities/${id}?workspace_id=${workspaceId}&version=${form.version}`, body);
      toast.success("Saved");
      load();
    } catch (err) {
      if (err.response?.status === 409) {
        toast.error("This record was modified by someone else. Reloading latest version.");
        load();
      } else toast.error(apiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const discard = () => {
    const d = full;
    setForm({ ...d.entity, tagsStr: (d.entity.tags || []).join(", "), aliasesStr: (d.entity.aliases || []).join(", "), websitesStr: (d.entity.websites || []).join(", ") });
    setDirty(false);
    toast.message("Changes discarded");
  };

  const archive = async () => {
    await api.delete(`/entities/${id}?workspace_id=${workspaceId}`); toast.success("Archived"); load();
  };
  const restore = async () => {
    await api.post(`/entities/${id}/restore?workspace_id=${workspaceId}`); toast.success("Restored"); load();
  };
  const del = async () => {
    try {
      const { data } = await api.get(`/entities/${id}/delete-preview?workspace_id=${workspaceId}`);
      setDelPreview(data);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Could not load deletion preview");
    }
  };
  const confirmDel = async () => {
    setDeleting(true);
    try {
      await api.delete(`/entities/${id}/permanent?workspace_id=${workspaceId}`);
      toast.success("Deleted");
      navigate("/app/entities");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Deletion failed");
      setDeleting(false);
    }
  };
  const promote = async () => {
    const { data } = await api.post(`/entities/${id}/promote-to-origin?workspace_id=${workspaceId}`);
    toast.success("Origin created"); navigate(`/app/spider-contact?origin=${data.id}`);
  };

  if (!full || !form) return <Loading />;
  const e = full.entity;

  return (
    <div className="p-6 md:p-8 max-w-5xl mx-auto">
      {canEdit && dirty && (
        <div className="sticky top-0 z-30 -mx-6 md:-mx-8 px-6 md:px-8 py-3 mb-4 flex items-center justify-between gap-3 flex-wrap"
          style={{ background: "var(--si-surface)", borderBottom: "1px solid var(--si-warning)", boxShadow: "0 4px 12px rgba(0,0,0,0.3)" }}
          data-testid="unsaved-bar">
          <span className="text-sm font-mono" style={{ color: "var(--si-warning)" }}>Unsaved changes</span>
          <div className="flex items-center gap-2">
            <Btn variant="secondary" onClick={discard} disabled={saving} data-testid="entity-discard"><RotateCcw size={15} /> Discard changes</Btn>
            <Btn variant="primary" onClick={save} disabled={saving} data-testid="entity-save-sticky"><Save size={15} /> {saving ? "Saving…" : "Save changes"}</Btn>
          </div>
        </div>
      )}
      <Link to="/app/entities" className="inline-flex items-center gap-1.5 text-sm mb-4" style={{ color: "var(--si-text-2)" }} data-testid="back-to-entities"><ArrowLeft size={15} /> Entity Files</Link>

      <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-2xl font-medium" style={{ color: "var(--si-text)" }}>{e.name}</h1>
            {e.archived && <Badge value="archived">archived</Badge>}
            {e.do_not_contact && <Badge value="disputed">do not contact</Badge>}
          </div>
          <div className="mt-2 flex gap-1.5 flex-wrap">
            <Badge value={e.type} /><Badge value={e.verification_status} /><Badge value={e.relationship_stage} />
            <span className="font-mono text-xs flex items-center" style={{ color: "var(--si-text-2)" }}>v{e.version}</span>
          </div>
        </div>
        {canEdit && (
          <div className="flex gap-2 flex-wrap">
            <Btn variant="primary" onClick={save} disabled={!dirty || saving} data-testid="entity-save"><Save size={15} /> {saving ? "Saving…" : dirty ? "Save changes" : "Saved"}</Btn>
            <Btn variant="secondary" onClick={() => { setTab("Identity"); window.scrollTo({ top: 0, behavior: "smooth" }); toast.message("Edit the fields below, then press Save changes."); }} data-testid="entity-edit-manual"><Pencil size={15} /> Edit Manually</Btn>
            <Btn variant="secondary" onClick={() => setVoiceEditOpen(true)} data-testid="entity-voice-update"><Mic size={15} /> Update by Voice</Btn>
            <Btn variant="ghost" onClick={promote} data-testid="entity-promote"><ArrowUpRight size={15} /> To origin</Btn>
            <Btn variant="ghost" onClick={() => setVoiceOpen(true)} data-testid="entity-voice-btn"><Plus size={15} /> Add Related Contact by Voice</Btn>
            <EmailButton workspaceId={workspaceId} entity={{ id, name: e.name }} emails={(e.contact_methods || []).map((c) => c.value).filter((v) => v && String(v).includes("@"))} />
            <Btn variant="ghost" onClick={async () => {
              try {
                const { data } = await api.get(`/entities/${id}/copy?workspace_id=${workspaceId}${isOwner ? "&include_restricted=true" : ""}`);
                await navigator.clipboard.writeText(data.text);
                toast.success("Full record copied to clipboard");
              } catch (err) { toast.error("Copy failed — your browser blocked clipboard access"); }
            }} data-testid="entity-copy-record" title="Copy full record (respects restricted-data permissions)"><Copy size={15} /> Copy</Btn>
            {e.archived ? <Btn variant="secondary" onClick={restore} data-testid="entity-restore"><RotateCcw size={15} /> Reactivate</Btn> : <Btn variant="secondary" onClick={archive} data-testid="entity-archive" title="Removes from active lists but keeps all relationships, evidence, memberships and history. Reactivate anytime."><Archive size={15} /> Archive</Btn>}
            <Btn variant="ghost" onClick={() => window.open(`${api.defaults.baseURL}/reports/contact/${id}.pdf?workspace_id=${workspaceId}&sections=overview,importance,personality,notes,commitments`, "_blank")} data-testid="entity-report" title="Contact dossier PDF"><FileDown size={15} /> Report</Btn>
            <Btn variant="ghost" onClick={() => window.open(`${api.defaults.baseURL}/reports/web/${id}.pdf?workspace_id=${workspaceId}&degrees=2`, "_blank")} data-testid="entity-web-report" title="Contact Web PDF (diagram + relationships)"><FileDown size={15} /> Web</Btn>
            <Btn variant="ghost" onClick={() => window.open(`${api.defaults.baseURL}/reports/summary/${id}.pdf?workspace_id=${workspaceId}&kind=prep`, "_blank")} data-testid="entity-prep-report" title="Prepare for Conversation PDF (cited)"><FileDown size={15} /> Prep</Btn>
            <Btn variant="ghost" onClick={() => window.open(`${api.defaults.baseURL}/reports/summary/${id}.pdf?workspace_id=${workspaceId}&kind=full`, "_blank")} data-testid="entity-full-report" title="Full Understanding PDF (cited)"><FileDown size={15} /> Full</Btn>
            {isOwner && !e.archived && <Btn variant="ghost" onClick={del} data-testid="entity-delete" title="Permanent delete (owner only) — irreversible. Archive is recommended instead." style={{ color: "var(--si-danger)" }}><Trash2 size={15} /></Btn>}
          </div>
        )}
      </div>

      <div className="flex gap-1 mb-6 flex-wrap" style={{ borderBottom: "1px solid var(--si-border)" }}>
        {TABS.map((t) => (
          <button key={t} data-testid={`tab-${t.toLowerCase()}`} onClick={() => setTab(t)} className="px-4 py-2 text-sm transition-colors duration-200"
            style={{ color: tab === t ? "var(--si-text)" : "var(--si-text-2)", borderBottom: tab === t ? "2px solid var(--si-teal-light)" : "2px solid transparent" }}>
            {t}{t === "Memberships" && ` (${full.memberships.length})`}{t === "Groups" && ` (${(full.groups || []).length})`}{t === "Relationships" && ` (${full.relationships.length})`}{t === "Evidence" && ` (${full.evidence.length})`}
          </button>
        ))}
      </div>

      {PROFILE_SECTION[tab] && (
        <ContactProfile section={PROFILE_SECTION[tab]} entityId={id} workspaceId={workspaceId} canEdit={canEdit} isOwner={isOwner} onReload={load} />
      )}

      {tab === "Identity" && (
        <div className="grid md:grid-cols-2 gap-x-6">
          <Field label="Name"><Input value={form.name} onChange={(e) => set("name", e.target.value)} disabled={!canEdit} data-testid="field-name" /></Field>
          <Field label="Type"><Select value={form.type} onChange={(e) => set("type", e.target.value)} disabled={!canEdit}>{ETYPES.map((t) => <option key={t} value={t}>{t}</option>)}</Select></Field>
          <Field label="Verification status" hint="Uncertain information must remain visibly uncertain."><Select value={form.verification_status} onChange={(e) => set("verification_status", e.target.value)} disabled={!canEdit} data-testid="field-verif">{VERIFS.map((t) => <option key={t} value={t}>{t}</option>)}</Select></Field>
          <Field label="Relationship stage"><Select value={form.relationship_stage} onChange={(e) => set("relationship_stage", e.target.value)} disabled={!canEdit}>{STAGES.map((t) => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}</Select></Field>
          <div className="md:col-span-2"><Field label="Aliases (comma separated)" hint="A matching name alone does not establish identity."><Input value={form.aliasesStr} onChange={(e) => set("aliasesStr", e.target.value)} disabled={!canEdit} /></Field></div>
          <div className="md:col-span-2"><Field label={<span className="flex items-center justify-between">Description {canEdit && <DictateButton workspaceId={workspaceId} onText={(t) => set("description", (form.description ? form.description + " " : "") + t)} title="Dictate description" />}</span>}><Textarea value={form.description} onChange={(e) => set("description", e.target.value)} disabled={!canEdit} /></Field></div>
          <Field label="Tags (comma separated)"><Input value={form.tagsStr} onChange={(e) => set("tagsStr", e.target.value)} disabled={!canEdit} /></Field>
          <Field label="Websites (comma separated)"><Input value={form.websitesStr} onChange={(e) => set("websitesStr", e.target.value)} disabled={!canEdit} /></Field>
          {canEdit && <div className="md:col-span-2"><Field label="Phone & email" hint="Stored canonically — searchable and used by Copy, reports, email Compose and backups."><ContactMethodsEditor key={id} value={form.contact_methods || []} onChange={(cm) => set("contact_methods", cm)} idPrefix="ed" /></Field></div>}
          <Field label="Address"><Input value={form.address || ""} onChange={(e) => set("address", e.target.value)} disabled={!canEdit} /></Field>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Latitude"><Input value={form.lat ?? ""} onChange={(e) => set("lat", e.target.value)} disabled={!canEdit} /></Field>
            <Field label="Longitude"><Input value={form.lng ?? ""} onChange={(e) => set("lng", e.target.value)} disabled={!canEdit} /></Field>
          </div>
          <Field label="Communication preference"><Input value={form.communication_preference || ""} onChange={(e) => set("communication_preference", e.target.value)} disabled={!canEdit} /></Field>
          <Field label="Do not contact">
            <label className="flex items-center gap-2 text-sm pt-1" style={{ color: "var(--si-text)" }}>
              <input type="checkbox" checked={!!form.do_not_contact} onChange={(e) => set("do_not_contact", e.target.checked)} disabled={!canEdit} /> Mark as do-not-contact
            </label>
          </Field>
          <div className="md:col-span-2"><Field label={<span className="flex items-center justify-between">Notes & relevance {canEdit && <DictateButton workspaceId={workspaceId} onText={(t) => set("notes", (form.notes ? form.notes + " " : "") + t)} title="Dictate notes" />}</span>}><Textarea value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} disabled={!canEdit} /></Field></div>
          <div className="md:col-span-2">
            <Field label="Strategic interests" hint="Associate this contact with strategic interests. Unknown relevance never removes a record.">
              <div className="flex flex-wrap gap-2">
                {interests.length === 0 && <span className="text-xs" style={{ color: "var(--si-text-2)" }}>No strategic interests defined yet — create them in Settings.</span>}
                {interests.map((si) => {
                  const on = (form.strategic_interest_ids || []).includes(si.id);
                  return (
                    <button key={si.id} data-testid={`si-chip-${si.id}`} disabled={!canEdit}
                      onClick={() => set("strategic_interest_ids", on ? (form.strategic_interest_ids || []).filter((x) => x !== si.id) : [...(form.strategic_interest_ids || []), si.id])}
                      className="px-2.5 py-1 text-xs font-mono transition-colors duration-200"
                      style={{ border: `1px solid ${on ? si.color || "var(--si-teal-light)" : "var(--si-border)"}`, color: on ? "var(--si-text)" : "var(--si-text-2)", background: on ? "var(--si-surface-hover)" : "transparent", borderRadius: 2 }}>
                      {si.name}
                    </button>
                  );
                })}
              </div>
            </Field>
          </div>
          <div className="md:col-span-2 font-mono text-xs" style={{ color: "var(--si-text-2)" }}>
            id {e.id} · created {new Date(e.created_at).toLocaleString()} · updated {new Date(e.updated_at).toLocaleString()}{e.last_verified_at && ` · verified ${new Date(e.last_verified_at).toLocaleDateString()}`}
          </div>
        </div>
      )}

      {tab === "Memberships" && (
        <div>
          {canEdit && <div className="mb-4"><Btn variant="secondary" onClick={() => setMemOpen(true)} data-testid="add-membership-btn"><Plus size={15} /> Add to network</Btn></div>}
          <Panel>
            {full.memberships.length === 0 ? <EmptyState icon={MapPin} title="Not in any network" hint="Add this entity to a Spider Contact origin." /> : (
              <div>
                {full.memberships.map((m) => (
                  <MembershipRow key={m.id} m={m} workspaceId={workspaceId} canEdit={canEdit} onChanged={load} />
                ))}
              </div>
            )}
          </Panel>
        </div>
      )}

      {tab === "Groups" && (
        <div>
          {canEdit && <div className="mb-4"><Btn variant="secondary" onClick={() => setGroupAddOpen(true)} data-testid="add-to-group-btn"><Plus size={15} /> Add to group</Btn></div>}
          <Panel>
            {(full.groups || []).length === 0 ? <EmptyState icon={Folder} title="Not in any group" hint="Add this contact to a group to organize it." /> : (
              <div>
                {full.groups.map((g) => (
                  <div key={g.membership_id} className="flex items-center justify-between px-5 py-3" style={{ borderBottom: "1px solid var(--si-border)" }} data-testid={`entity-group-${g.group_id}`}>
                    <div>
                      <Link to={`/app/groups?group=${g.group_id}`} className="text-sm" style={{ color: "var(--si-text)" }}>{g.group_name}</Link>
                      <div className="si-label mt-0.5 flex items-center gap-1">
                        {(g.folder_path || []).length > 0 && <><Folder size={10} /> {g.folder_path.map((p) => p.name).join(" / ")} · </>}
                        {g.note || "no membership note"}
                      </div>
                    </div>
                    {canEdit && <button onClick={async () => { await api.delete(`/groups/${g.group_id}/members/${id}?workspace_id=${workspaceId}`); toast.success("Removed from group"); load(); }} title="Remove from group" style={{ color: "var(--si-text-2)" }} data-testid={`entity-group-remove-${g.group_id}`}><X size={14} /></button>}
                  </div>
                ))}
              </div>
            )}
          </Panel>
        </div>
      )}

      {tab === "Web" && (
        <ContactWeb entityId={id} entityName={e.name} workspaceId={workspaceId} canEdit={canEdit}
          onOpenEntity={(eid) => navigate(`/app/entities/${eid}`)} onChanged={load} />
      )}

      {tab === "Relationships" && (
        <div>
          {canEdit && <div className="mb-4"><Btn variant="secondary" onClick={() => setRelOpen(true)} data-testid="add-rel-btn"><Plus size={15} /> Add connection</Btn></div>}
          <Panel>
            {full.relationships.length === 0 ? <EmptyState icon={GitBranch} title="No relationships documented" /> : (
              full.relationships.map((r) => (
                <div key={r.id} className="flex items-center justify-between px-5 py-3 text-sm" style={{ borderBottom: "1px solid var(--si-border)" }}>
                  <div style={{ color: "var(--si-text)" }}>
                    {r.source_name} <span className="font-mono mx-1" style={{ color: "var(--si-silver)" }}>{r.direction === "mutual" ? "↔" : "→"} {r.type === "other" && r.custom_label ? r.custom_label : r.type.replace(/_/g, " ")}</span> {r.target_name}
                    {r.notes && <div className="text-xs mt-0.5" style={{ color: "var(--si-text-2)" }}>{r.notes}</div>}
                    {r.source && <div className="si-label mt-0.5">source: {r.source}</div>}
                  </div>
                  <div className="flex items-center gap-2"><Badge value={r.status} />{canEdit && <button onClick={() => setRelEdit(r)} title="Edit connection" style={{ color: "var(--si-text-2)" }} data-testid={`rel-edit-${r.id}`}><Pencil size={14} /></button>}{canEdit && <button onClick={async () => { await api.delete(`/relationships/${r.id}?workspace_id=${workspaceId}`); load(); }} title="Remove connection" style={{ color: "var(--si-text-2)" }} data-testid={`rel-del-${r.id}`}><X size={14} /></button>}</div>
                </div>
              ))
            )}
          </Panel>
        </div>
      )}

      {tab === "Evidence" && (
        <div>
          {canEdit && <div className="mb-4"><Btn variant="secondary" onClick={() => setEvOpen(true)} data-testid="add-ev-btn"><Plus size={15} /> Add evidence / observation</Btn></div>}
          <Panel>
            {full.evidence.length === 0 ? <EmptyState icon={FlaskConical} title="No evidence attached" hint="Add a source URL or record a direct observation." /> : (
              full.evidence.map((ev) => (
                <div key={ev.id} className="px-5 py-3" style={{ borderBottom: "1px solid var(--si-border)" }}>
                  <div className="flex items-center gap-2 flex-wrap"><Badge value={ev.classification} /><Badge value={ev.verification_status} /><span className="si-label">{ev.source_kind}</span>{ev.field_ref && <span className="si-label">→ {ev.field_ref}</span>}</div>
                  {ev.excerpt && <div className="text-sm mt-1.5" style={{ color: "var(--si-text)" }}>{ev.excerpt}</div>}
                  {ev.source_url && safeHref(ev.source_url) && <a href={safeHref(ev.source_url)} target="_blank" rel="noreferrer" className="text-xs font-mono flex items-center gap-1 mt-1" style={{ color: "var(--si-silver)" }}><Link2 size={11} /> {ev.source_url}</a>}
                  <div className="si-label mt-1">{ev.retrieval_date ? new Date(ev.retrieval_date).toLocaleDateString() : ""}{canEdit && <button className="ml-3" onClick={() => setEvEdit(ev)} style={{ color: "var(--si-silver)" }} data-testid={`evidence-edit-${ev.id}`}>edit</button>}{canEdit && <button className="ml-3" onClick={async () => { await api.delete(`/evidence/${ev.id}?workspace_id=${workspaceId}`); load(); }} style={{ color: "var(--si-danger)" }} data-testid={`evidence-del-${ev.id}`}>remove</button>}</div>
                </div>
              ))
            )}
          </Panel>
        </div>
      )}

      {tab === "Activity" && (
        <Panel>
          {full.activities.length === 0 ? <EmptyState icon={History} title="No activity yet" hint="Create a task or follow-up from the Actions section." /> : (
            full.activities.map((a) => (
              <div key={a.id} className="flex items-center justify-between px-5 py-3 text-sm" style={{ borderBottom: "1px solid var(--si-border)" }}>
                <div><span className="si-label mr-2">{a.kind}</span><span style={{ color: "var(--si-text)" }}>{a.title}</span>{a.outcome && <div className="text-xs mt-0.5" style={{ color: "var(--si-text-2)" }}>→ {a.outcome}</div>}</div>
                <Badge value={a.status === "done" ? "confirmed" : "pending"}>{a.status}</Badge>
              </div>
            ))
          )}
        </Panel>
      )}

      {tab === "History" && (
        <Panel>
          {audit.length === 0 ? <EmptyState icon={History} title="No audit history" /> : (
            audit.map((a) => (
              <div key={a.id} className="px-5 py-2.5 text-sm font-mono" style={{ borderBottom: "1px solid var(--si-border)" }}>
                <span style={{ color: "var(--si-teal-light)" }}>{a.action}</span> <span style={{ color: "var(--si-text-2)" }}>by {a.user_email} · {new Date(a.created_at).toLocaleString()}</span>
              </div>
            ))
          )}
        </Panel>
      )}

      {relOpen && <ConnectionModal workspaceId={workspaceId} source={{ id, name: e.name }} onClose={() => setRelOpen(false)} onSaved={() => { setRelOpen(false); load(); }} />}
      {relEdit && <ConnectionModal workspaceId={workspaceId} source={{ id, name: e.name }} editRel={relEdit} onClose={() => setRelEdit(null)} onSaved={() => { setRelEdit(null); load(); }} />}
      {voiceOpen && <VoiceContactModal workspaceId={workspaceId} sourceEntity={{ id, name: e.name }} onClose={() => setVoiceOpen(false)} onSaved={load} />}
      {voiceEditOpen && <VoiceEditModal workspaceId={workspaceId} entityId={id} entityName={e.name} onClose={() => setVoiceEditOpen(false)} onSaved={load} />}
      {evOpen && <EvModal workspaceId={workspaceId} entityId={id} onClose={() => setEvOpen(false)} onSaved={() => { setEvOpen(false); load(); }} />}
      {evEdit && <EvModal workspaceId={workspaceId} entityId={id} editEv={evEdit} onClose={() => setEvEdit(null)} onSaved={() => { setEvEdit(null); load(); }} />}
      {memOpen && <MembershipModal workspaceId={workspaceId} entityId={id} existing={full.memberships} onClose={() => setMemOpen(false)} onSaved={() => { setMemOpen(false); load(); }} />}
      {groupAddOpen && <EntityGroupAddModal workspaceId={workspaceId} entityId={id} existing={full.groups || []} onClose={() => setGroupAddOpen(false)} onSaved={() => { setGroupAddOpen(false); load(); }} />}

      {delPreview && (
        <Modal open onClose={() => !deleting && setDelPreview(null)} title="Permanently delete entity" testid="entity-delete-modal">
          <p className="text-sm mb-3" style={{ color: "var(--si-text-2)" }}>
            You are about to permanently delete <strong style={{ color: "var(--si-text)" }}>{delPreview.entity.name}</strong>. This cannot be undone. The following linked records will be affected:
          </p>
          <div className="space-y-1.5 text-sm mb-4" data-testid="delete-affected-summary">
            <div className="flex justify-between"><span>Relationships removed</span><span className="font-mono" data-testid="del-count-relationships">{delPreview.counts.relationships}</span></div>
            <div className="flex justify-between"><span>Origin memberships removed</span><span className="font-mono" data-testid="del-count-memberships">{delPreview.counts.memberships}</span></div>
            <div className="flex justify-between"><span>Evidence records removed</span><span className="font-mono" data-testid="del-count-evidence">{delPreview.counts.evidence}</span></div>
            <div className="flex justify-between"><span>Tasks unlinked (kept)</span><span className="font-mono" data-testid="del-count-activities">{delPreview.counts.activities}</span></div>
          </div>
          {delPreview.counts.activities_unlinked > 0 && (
            <p className="text-xs mb-4" style={{ color: "var(--si-text-2)" }}>
              {delPreview.counts.activities_unlinked} task(s) reference only this entity and will become general workspace tasks (not deleted).
            </p>
          )}
          <div className="flex gap-2 justify-end">
            <Btn variant="secondary" onClick={() => setDelPreview(null)} disabled={deleting}>Cancel</Btn>
            <Btn variant="danger" onClick={confirmDel} disabled={deleting} data-testid="entity-delete-confirm"><Trash2 size={15} /> {deleting ? "Deleting…" : "Delete permanently"}</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

function EvModal({ workspaceId, entityId, editEv, onClose, onSaved }) {
  const isEdit = !!editEv;
  const [f, setF] = useState(isEdit
    ? { source_kind: editEv.source_kind || "observation", source_url: editEv.source_url || "", excerpt: editEv.excerpt || "", classification: editEv.classification || "observation", verification_status: editEv.verification_status || "uncertain", field_ref: editEv.field_ref || "" }
    : { source_kind: "observation", source_url: "", excerpt: "", classification: "observation", verification_status: "uncertain", field_ref: "" });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => {
    try {
      if (isEdit) {
        await api.put(`/evidence/${editEv.id}?workspace_id=${workspaceId}`, { ...f, entity_id: entityId, retrieval_date: editEv.retrieval_date || new Date().toISOString() });
        toast.success("Evidence updated"); onSaved();
      } else {
        await api.post(`/evidence?workspace_id=${workspaceId}`, { ...f, entity_id: entityId, retrieval_date: new Date().toISOString() });
        toast.success("Evidence added"); onSaved();
      }
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  return (
    <Modal open onClose={onClose} title={isEdit ? "Edit evidence / observation" : "Add evidence / observation"} testid="ev-modal">
      <Field label="Source kind"><Select value={f.source_kind} onChange={(e) => set("source_kind", e.target.value)}>{["observation", "source_url", "document", "import"].map((t) => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}</Select></Field>
      {f.source_kind === "source_url" && <Field label="Source URL"><Input value={f.source_url} onChange={(e) => set("source_url", e.target.value)} placeholder="https://…" /></Field>}
      <Field label="Field reference (optional)" hint="Attach to a specific field, e.g. 'ownership', 'address'."><Input value={f.field_ref} onChange={(e) => set("field_ref", e.target.value)} /></Field>
      <Field label="Excerpt / note"><Textarea value={f.excerpt} onChange={(e) => set("excerpt", e.target.value)} /></Field>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Classification"><Select value={f.classification} onChange={(e) => set("classification", e.target.value)}>{["fact", "observation", "inference"].map((t) => <option key={t} value={t}>{t}</option>)}</Select></Field>
        <Field label="Verification"><Select value={f.verification_status} onChange={(e) => set("verification_status", e.target.value)}>{["uncertain", "confirmed", "disputed", "outdated"].map((t) => <option key={t} value={t}>{t}</option>)}</Select></Field>
      </div>
      <Btn variant="primary" className="w-full" onClick={save} data-testid="ev-save">{isEdit ? "Save changes" : "Add evidence"}</Btn>
    </Modal>
  );
}

const REVIEW_STATUSES = ["included", "pending", "excluded"];

function EntityGroupAddModal({ workspaceId, entityId, existing, onClose, onSaved }) {
  const [groups, setGroups] = useState([]);
  const [groupId, setGroupId] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get(`/groups?workspace_id=${workspaceId}`).then((r) => setGroups(r.data.items || [])).catch(() => {}); }, [workspaceId]);
  const existingIds = new Set((existing || []).map((g) => g.group_id));
  const available = groups.filter((g) => !existingIds.has(g.id));
  const save = async () => {
    if (!groupId) return toast.error("Pick a group");
    setBusy(true);
    try {
      await api.post(`/groups/${groupId}/members?workspace_id=${workspaceId}`, { entity_ids: [entityId], note });
      toast.success("Added to group"); onSaved();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); setBusy(false); }
  };
  return (
    <Modal open onClose={onClose} title="Add to group" testid="entity-group-add-modal">
      {available.length === 0 ? (
        <EmptyState icon={Folder} title="No groups available" hint="This contact is in every group, or none exist yet. Create one in Groups & Folders." />
      ) : (
        <>
          <Field label="Group"><Select value={groupId} onChange={(e) => setGroupId(e.target.value)} data-testid="entity-group-select"><option value="">— select —</option>{available.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}</Select></Field>
          <Field label="Membership note (optional)"><Input value={note} onChange={(e) => setNote(e.target.value)} data-testid="entity-group-note" /></Field>
          <Btn variant="primary" className="w-full" onClick={save} disabled={busy} data-testid="entity-group-add-save">{busy ? "Adding…" : "Add to group"}</Btn>
        </>
      )}
    </Modal>
  );
}


function MembershipRow({ m, workspaceId, canEdit, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [f, setF] = useState({ discovery_path: m.discovery_path || "", source: m.source || "", review_status: m.review_status || "included" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await api.put(`/memberships/${m.id}?workspace_id=${workspaceId}`, f); toast.success("Membership updated"); setEditing(false); onChanged(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const remove = async () => {
    try { await api.delete(`/memberships/${m.id}?workspace_id=${workspaceId}`); toast.success("Removed from network"); onChanged(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  if (editing) {
    return (
      <div className="px-5 py-3 space-y-2" style={{ borderBottom: "1px solid var(--si-border)" }} data-testid={`membership-edit-${m.id}`}>
        <div className="text-sm font-medium" style={{ color: "var(--si-text)" }}>{m.origin_name}</div>
        <div className="grid md:grid-cols-3 gap-2">
          <Field label="Discovery path"><Input value={f.discovery_path} onChange={(e) => setF({ ...f, discovery_path: e.target.value })} data-testid={`membership-path-${m.id}`} /></Field>
          <Field label="Source"><Input value={f.source} onChange={(e) => setF({ ...f, source: e.target.value })} data-testid={`membership-source-${m.id}`} /></Field>
          <Field label="Review status"><Select value={f.review_status} onChange={(e) => setF({ ...f, review_status: e.target.value })} data-testid={`membership-status-${m.id}`}>{REVIEW_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}</Select></Field>
        </div>
        <div className="flex gap-2">
          <Btn variant="primary" className="!py-1 !px-3 text-xs" onClick={save} disabled={busy} data-testid={`membership-save-${m.id}`}><Save size={13} /> {busy ? "Saving…" : "Save"}</Btn>
          <Btn variant="ghost" className="!py-1 !px-3 text-xs" onClick={() => setEditing(false)}>Cancel</Btn>
        </div>
      </div>
    );
  }
  return (
    <div className="flex items-center justify-between px-5 py-3" style={{ borderBottom: "1px solid var(--si-border)" }} data-testid={`membership-${m.id}`}>
      <div>
        <Link to={`/app/spider-contact?origin=${m.origin_id}`} className="text-sm" style={{ color: "var(--si-text)" }}>{m.origin_name}</Link>
        <div className="si-label mt-0.5">{m.discovery_path || "—"} · {m.source || "—"}{m.distance_m != null ? ` · ${m.distance_m}m` : ""}</div>
      </div>
      <div className="flex items-center gap-2">
        <Badge value={m.review_status} />
        {canEdit && <button onClick={() => setEditing(true)} title="Edit membership" style={{ color: "var(--si-text-2)" }} data-testid={`membership-edit-btn-${m.id}`}><Pencil size={14} /></button>}
        {canEdit && <button onClick={remove} title="Remove from network" style={{ color: "var(--si-text-2)" }} data-testid={`membership-del-${m.id}`}><X size={14} /></button>}
      </div>
    </div>
  );
}

function MembershipModal({ workspaceId, entityId, existing, onClose, onSaved }) {
  const [origins, setOrigins] = useState([]);
  const [f, setF] = useState({ origin_id: "", discovery_path: "", source: "", review_status: "included" });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get(`/origins?workspace_id=${workspaceId}&limit=200`).then((r) => setOrigins(r.data.items || [])).catch(() => {});
  }, [workspaceId]);
  const existingIds = new Set((existing || []).map((m) => m.origin_id));
  const available = origins.filter((o) => !existingIds.has(o.id));
  const save = async () => {
    if (!f.origin_id) return toast.error("Pick a network / origin");
    setBusy(true);
    try {
      await api.post(`/memberships?workspace_id=${workspaceId}`, { ...f, entity_id: entityId });
      toast.success("Added to network"); onSaved();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); setBusy(false); }
  };
  return (
    <Modal open onClose={onClose} title="Add to network" testid="membership-modal">
      {available.length === 0 ? (
        <EmptyState icon={MapPin} title="No networks available" hint="This contact is already in every origin, or none exist yet. Create one in Spider Contact." />
      ) : (
        <>
          <Field label="Network / origin"><Select value={f.origin_id} onChange={(e) => setF({ ...f, origin_id: e.target.value })} data-testid="membership-origin-select"><option value="">— select —</option>{available.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}</Select></Field>
          <Field label="Discovery path (optional)" hint="How this contact was found within the network."><Input value={f.discovery_path} onChange={(e) => setF({ ...f, discovery_path: e.target.value })} data-testid="membership-new-path" /></Field>
          <Field label="Source (optional)"><Input value={f.source} onChange={(e) => setF({ ...f, source: e.target.value })} data-testid="membership-new-source" /></Field>
          <Field label="Review status"><Select value={f.review_status} onChange={(e) => setF({ ...f, review_status: e.target.value })} data-testid="membership-new-status">{REVIEW_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}</Select></Field>
          <Btn variant="primary" className="w-full" onClick={save} disabled={busy} data-testid="membership-add-save">{busy ? "Adding…" : "Add to network"}</Btn>
        </>
      )}
    </Modal>
  );
}
